import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CanFallback } from './components/CanFallback'
import { AssetLoader } from './components/AssetLoader'
import { ArtifactArchive } from './components/ArtifactArchive'
import { TransmissionRoom } from './components/TransmissionRoom'
import { SignalFinale } from './components/SignalFinale'
import { IgnitionSequence } from './components/IgnitionSequence'
import { NovaLogo } from './components/NovaLogo'
import { SceneErrorBoundary } from './components/SceneErrorBoundary'
import { NOVA_FLAVOURS } from './data/flavours'
import { useMotionPreference } from './hooks/useMotionPreference'
import { useFlavourDeck } from './hooks/useFlavourDeck'
import { useNovaMotion } from './hooks/useNovaMotion'
import { useSfxAudio } from './hooks/useSfxAudio'
import {
  canUseWebGL,
  prefersDataSaving,
} from './utils/webgl'

const NovaScene = lazy(() => import('./three/NovaScene.lazy'))

function FourPointStar({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M32 2c3.5 17.4 12.6 26.5 30 30-17.4 3.5-26.5 12.6-30 30C28.5 44.6 19.4 35.5 2 32 19.4 28.5 28.5 19.4 32 2Z" />
    </svg>
  )
}

function SignalStripGroup({ isClone = false }: { isClone?: boolean }) {
  return (
    <div
      className="signal-strip__group"
      aria-hidden={isClone ? true : undefined}
    >
      <span>DEEP SPACE FLAVOUR</span>
      <FourPointStar />
      <span>ANCIENT SPARK FLAVOUR</span>
      <FourPointStar />
      <span>EVENT HORIZON FLAVOUR</span>
      <FourPointStar />
      <span>THREE SIGNALS / ONE SOURCE</span>
      <FourPointStar />
    </div>
  )
}

function App() {
  const pageRef = useRef<HTMLDivElement>(null)
  const { motionEnabled } = useMotionPreference()
  // Audio starts only after an explicit click, never on page load or scroll.
  const [sfxEnabled, setSfxEnabled] = useState(false)
  const [sceneSupported] = useState(
    () => !prefersDataSaving() && canUseWebGL(),
  )
  const [sceneReady, setSceneReady] = useState(false)
  const [sceneFailed, setSceneFailed] = useState(false)
  const [assetProgress, setAssetProgress] = useState({ completed: 0, total: 5 })
  const scenePending = motionEnabled && sceneSupported && !sceneReady && !sceneFailed

  useNovaMotion(pageRef, motionEnabled && !scenePending)
  useFlavourDeck(pageRef, motionEnabled && !scenePending)
  useSfxAudio(pageRef, sfxEnabled)

  const handleToggleSfx = useCallback(() => {
    setSfxEnabled((enabled) => !enabled)
  }, [])

  const handleSceneReady = useCallback(() => {
    setSceneReady(true)
    setSceneFailed(false)
  }, [])

  const handleSceneFailure = useCallback(() => {
    setSceneReady(false)
    setSceneFailed(true)
  }, [])

  const handleSceneDisposed = useCallback(() => {
    // Reacquire readiness if the system changes its reduced-motion preference.
    setSceneReady(false)
  }, [])

  const handleAssetProgress = useCallback((completed: number, total: number) => {
    setAssetProgress({ completed, total })
  }, [])

  useEffect(() => {
    if (!scenePending) return
    const timeout = window.setTimeout(handleSceneFailure, 25_000)
    return () => window.clearTimeout(timeout)
  }, [handleSceneFailure, scenePending])

  return (
    <div
      ref={pageRef}
      className="site-shell"
      data-motion={motionEnabled ? 'on' : 'off'}
      data-sfx={sfxEnabled ? 'on' : 'off'}
      data-assets={scenePending ? 'loading' : sceneReady ? 'ready' : 'lightweight'}
    >
      <AssetLoader
        loading={scenePending}
        completed={assetProgress.completed}
        total={assetProgress.total}
        onSkip={handleSceneFailure}
      />
      <div className="capture-line" aria-hidden="true">
        <span className="capture-line__beam" />
        <span className="capture-line__charge" />
      </div>

      <header className="site-header" inert={scenePending}>
        <a className="site-header__brand" href="#top" aria-label="Nova Energy home">
          <NovaLogo />
        </a>

        <nav className="site-nav" aria-label="Primary navigation">
          <a href="#origin">Origin</a>
          <a href="#flavour">Flavours</a>
          <a className="site-nav__film" href="#film">
            Film <span aria-hidden="true">↘</span>
          </a>
          <a href="#artifact">Artifact</a>
        </nav>

        <div className="site-header__controls">
          <button
            className="sfx-toggle"
            type="button"
            onClick={handleToggleSfx}
            aria-label="Sound effects"
            aria-pressed={sfxEnabled}
            title={sfxEnabled ? 'Disable sound effects' : 'Enable sound effects'}
          >
            <span>SFX</span>
            <span className="sfx-toggle__state" aria-hidden="true">
              {sfxEnabled ? 'ON' : 'OFF'}
            </span>
          </button>
        </div>
      </header>

      <main id="main-content" inert={scenePending} aria-busy={scenePending}>
        <div className="can-journey" data-can-journey>
          <div
            className="can-journey__sticky"
            aria-label="Nova Energy can changing through three recovered flavours"
          >
            <div className="can-journey__ambient can-journey__ambient--deep" aria-hidden="true" />
            <div className="can-journey__ambient can-journey__ambient--ancient" aria-hidden="true" />
            <div className="can-journey__ambient can-journey__ambient--event" aria-hidden="true" />

            <div className="hero__stage-shell can-journey__stage-shell">
              <div className="hero__stage-glow" aria-hidden="true" />
              <svg
                className="hero-orbit"
                viewBox="0 0 620 720"
                aria-hidden="true"
              >
                <ellipse
                  className="hero-orbit__path"
                  cx="310"
                  cy="360"
                  rx="218"
                  ry="324"
                />
                <path
                  className="hero-orbit__path"
                  d="M310 18v684M0 360h620"
                />
                <circle cx="310" cy="360" r="10" />
              </svg>

              {!motionEnabled || !sceneSupported || sceneFailed ? <CanFallback /> : null}

              {motionEnabled && sceneSupported && !sceneFailed ? (
                <SceneErrorBoundary fallback={null} onFailure={handleSceneFailure}>
                  <Suspense fallback={null}>
                    <NovaScene
                      onReady={handleSceneReady}
                      onFailure={handleSceneFailure}
                      onProgress={handleAssetProgress}
                      onDispose={handleSceneDisposed}
                    />
                  </Suspense>
                </SceneErrorBoundary>
              ) : null}

              <div className="hero__stage-star" aria-hidden="true">
                <FourPointStar />
              </div>
            </div>
            <span className="sr-only">
              A near-black Nova Energy can retuned from ultraviolet to ion blue
              and solar orange as the page scrolls.
            </span>
          </div>

        <section
          className="hero"
          id="top"
          aria-labelledby="hero-title"
          data-scene-step="hero"
        >
          <div className="hero__wash" aria-hidden="true" />
          <div className="hero__grid">
            <div className="hero__copy">
              <p className="signal-label hero__signal">
                NOVA ENERGY <span>//</span> SIGNAL NVA–01
              </p>

              <h1 className="hero__title" id="hero-title">
                <span className="hero__line">
                  <span className="hero__line-inner">Stolen</span>
                </span>
                <span className="hero__line">
                  <span className="hero__line-inner">from the</span>
                </span>
                <span className="hero__line hero__line--last">
                  <span className="hero__line-inner">skies.</span>
                </span>
              </h1>

              <div className="hero__meta">
                <p>
                  Universe's flavours,
                  <br />
                  brought down to Earth.
                </p>
                <div className="hero__actions">
                  <a className="button button--primary" href="#film">
                    Enter transmission
                    <span aria-hidden="true">↘</span>
                  </a>
                  <a className="text-link" href="#flavour">
                    More signals <span aria-hidden="true">↓</span>
                  </a>
                </div>
              </div>
            </div>

          </div>

          <div className="hero__coordinates" aria-hidden="true">
            <span>CAPTURED / 08.14.22</span>
            <span>CLASS / CARBONATED ARTIFACT</span>
          </div>

          <a className="hero__scroll" href="#origin">
            <span>Follow the signal</span>
            <span className="hero__scroll-line" aria-hidden="true" />
          </a>
        </section>

        <section className="signal-strip" aria-label="Nova Energy manifesto">
          <div className="signal-strip__track">
            <SignalStripGroup />
            <SignalStripGroup isClone />
          </div>
        </section>

        <section
          className="origin section"
          id="origin"
          aria-labelledby="origin-title"
          data-scene-step="origin"
        >
          <div className="section__header" data-reveal>
            <p className="signal-label">ORIGIN // UNKNOWN</p>
            <p className="section__index">RECOVERED SIGNAL / A</p>
          </div>

          <div className="origin__layout">
            <h2 className="origin__statement" id="origin-title">
              <span className="origin__statement-line">Not</span>
              <span className="origin__statement-line">made</span>
              <span className="origin__statement-line">here.</span>
            </h2>

            <div className="origin__copy" data-reveal>
              <p className="origin__lede">
                A black-violet signal, captured at the edge of the universe. <br />
                Recreated and sealed under pressure.
              </p>
              <p>
                Nova began as a transmission with no known source. What came
                through was cold, bright and impossible to place — so we put it
                in a can.
              </p>

              <dl className="origin__data">
                <div>
                  <dt>Object</dt>
                  <dd>NVA–01</dd>
                </div>
                <div>
                  <dt>Finish</dt>
                  <dd>Ultraviolet</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>Contained</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <section
          className="flavour-voyage"
          id="flavour"
          aria-labelledby="flavour-title"
        >
          <div className="flavour-voyage__navigation">
            <div className="flavour-voyage__gesture" aria-hidden="true">
              <div className="flavour-voyage__charge">
                <span><i /></span>
                <span><i /></span>
                <span><i /></span>
              </div>
              <div className="flavour-voyage__gesture-copy">
                <svg
                  className="flavour-voyage__gesture-icon flavour-voyage__gesture-icon--mouse"
                  viewBox="0 0 28 38"
                >
                  <rect x="4" y="2" width="20" height="32" rx="10" />
                  <path className="flavour-voyage__gesture-motion" d="M14 8v8" />
                  <path d="m10 13 4 4 4-4" />
                </svg>
                <svg
                  className="flavour-voyage__gesture-icon flavour-voyage__gesture-icon--touch"
                  viewBox="0 0 32 40"
                >
                  <path d="M13 21V8a3 3 0 0 1 6 0v8l2-1a3 3 0 0 1 4 2l2 9c.8 4-2.2 8-6.3 8H15c-2 0-3.8-.9-5-2.5L5 25a3 3 0 0 1 4.7-3.7L13 25" />
                  <path className="flavour-voyage__gesture-motion" d="M7 13 3 9m4 4 4-4M7 13V2" />
                </svg>
                <span className="flavour-voyage__gesture-label flavour-voyage__gesture-label--desktop">
                  Scroll to charge
                </span>
                <span className="flavour-voyage__gesture-label flavour-voyage__gesture-label--mobile">
                  Swipe ×3
                </span>
              </div>
            </div>
            <div className="flavour-voyage__controls" aria-label="Flavour navigation">
              <button type="button" data-deck-direction="previous" aria-label="Previous frequency">
                <span aria-hidden="true">←</span> Back
              </button>
              <button type="button" data-deck-direction="next" aria-label="Next frequency">
                Next <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>

          <div className="flavour-voyage__intro">
            <div className="section__header" data-reveal>
              <p className="signal-label">RECOVERED FREQUENCIES // NVA-01-03</p>
              <p className="section__index">FLAVOUR ARRAY / B</p>
            </div>
            <h2 id="flavour-title" data-reveal>
              Three signals.
              <br />
              <span>One impossible source.</span>
            </h2>
            <p data-reveal>
              Scroll to retune the artifact. One form, three frequencies pulled
              from the same unknown sky.
            </p>
          </div>

          <div className="flavour-voyage__steps">
            {NOVA_FLAVOURS.map((flavour, index) => (
              <article
                className="flavour-step"
                data-flavour={flavour.id}
                data-flavour-step
                data-scene-step={flavour.id}
                aria-labelledby={`flavour-${flavour.id}-title`}
                key={flavour.id}
              >
                <span
                  className="flavour-step__snap"
                  aria-hidden="true"
                />
                <span
                  className="flavour-step__anchor"
                  id={`flavour-${flavour.id}`}
                  aria-hidden="true"
                />

                <div className="flavour-step__underlay" aria-hidden="true">
                  <p className="flavour-step__ordinal">
                    {String(index + 1).padStart(2, '0')}
                    <span>/ 03</span>
                  </p>
                  <div className="flavour-step__field" />
                </div>

                <div className="flavour-step__foreground">
                  <div className="flavour-step__copy">
                    <p className="signal-label">
                      {flavour.code} // {flavour.frequency}
                    </p>
                    <h3 id={`flavour-${flavour.id}-title`}>{flavour.name}</h3>
                    <p>{flavour.description}</p>
                    <div className="flavour-step__trace">
                      <span>Trace</span>
                      <strong>{flavour.frequency}</strong>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <p className="flavour-voyage__exit signal-label">
            TRACE COMPLETE // ENTER RELEASE PROTOCOL
          </p>
        </section>
        </div>

        <IgnitionSequence
          motionEnabled={motionEnabled}
          sceneEnabled={sceneSupported}
        />

        <TransmissionRoom motionEnabled={motionEnabled} />
        <ArtifactArchive motionEnabled={motionEnabled} />
        <SignalFinale motionEnabled={motionEnabled} />
      </main>

      <footer className="site-footer" inert={scenePending}>
        <NovaLogo compact />
        <p>A fictional brand experience / 2026</p>
        <p>Three recovered frequencies</p>
      </footer>
    </div>
  )
}

export default App
